import { formatDistanceToNow } from "date-fns";
import { AlertCircle, KeyRound, Send, Trash2, UserPlus, X } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { useForm } from "react-hook-form";

import { ConfirmDialog, DataTable, ErrorState } from "../../components/data/index.js";
import { FieldStack } from "../../components/forms/index.js";
import { MultiStorePicker } from "../../components/forms/MultiStorePicker.js";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert.js";
import { PageHeader } from "../../components/layout/index.js";
import { Button } from "../../components/ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog.js";
import { Input } from "../../components/ui/input.js";
import { Label } from "../../components/ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select.js";
import {
  useCreateInvitationMutation,
  useInvitationsListQuery,
  useReissueInvitationMutation,
  useRevokeInvitationMutation,
  type CreateInvitationRequest,
  type InvitationSummary,
} from "../../lib/api/invitations.js";
import {
  isStoreScopedRole,
  useAdminResetPasswordMutation,
  useRemoveCompanyUserMutation,
  useTenantUsersQuery,
  useUnboundStoreScopedUsersQuery,
  useUpdateCompanyUserRoleMutation,
  type TenantUserInfo,
  type UserRole,
} from "../../lib/api/users.js";
import { useInvitePolicy } from "../../lib/auth/invite-policy.js";
import { toast } from "../../lib/toast.js";
import { useAuth } from "../../providers/auth-provider.js";

export const handle = { crumb: "Team" };

const ROLE_LABELS: Record<UserRole, string> = {
  tenant_admin: "Admin",
  ops_manager: "Ops manager",
  store_manager: "Store manager",
  store_associate: "Store associate",
  field_auditor: "Field auditor",
  viewer: "Viewer",
};

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

interface InviteForm {
  email: string;
  role: UserRole;
  storeIds: string[];
}

export function UsersPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin";
  const isStoreManager = user?.role === "store_manager";
  // ops_manager retains read-only access (legacy behavior). Other roles see 403.
  const canView = isAdmin || isStoreManager || user?.role === "ops_manager";

  const tenantId = user?.tenantId ?? "";
  const tenantUsersQuery = useTenantUsersQuery(tenantId, { enabled: canView && tenantId !== "" });
  const invitationsQuery = useInvitationsListQuery({
    enabled: isAdmin || isStoreManager,
  });
  const unboundQuery = useUnboundStoreScopedUsersQuery({ enabled: isAdmin });
  const policy = useInvitePolicy();
  const updateRole = useUpdateCompanyUserRoleMutation();
  const adminReset = useAdminResetPasswordMutation();
  const revoke = useRevokeInvitationMutation();
  const reissue = useReissueInvitationMutation();
  const removeMember = useRemoveCompanyUserMutation({
    onSuccess: () => toast.success("Member removed"),
    onError: (e) => toast.error(e.problem?.detail ?? "Couldn't remove member"),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const defaultInviteRole: InviteForm["role"] = policy.allowedRoles[0] ?? "viewer";
  const inviteForm = useForm<InviteForm>({
    defaultValues: { email: "", role: defaultInviteRole, storeIds: [] },
  });
  const invite = useCreateInvitationMutation({
    onSuccess: (data) => {
      toast.success("Invitation sent", data.devLink ? `Dev link: ${data.devLink}` : undefined);
      setInviteOpen(false);
      inviteForm.reset({ email: "", role: defaultInviteRole, storeIds: [] });
      void invitationsQuery.refetch();
      void tenantUsersQuery.refetch();
    },
    onError: (e) => toast.error(e.problem?.detail ?? "Failed to send invitation"),
  });

  const selectedRole = inviteForm.watch("role");
  const selectedStoreIds = inviteForm.watch("storeIds");
  const showStorePicker = useMemo(
    () => isStoreScopedRole(selectedRole as UserRole),
    [selectedRole],
  );

  if (!canView) {
    return <ErrorState error={new Error("You don't have permission to view team members.")} />;
  }
  if (tenantUsersQuery.isError) {
    return <ErrorState error={tenantUsersQuery.error} onRetry={() => void tenantUsersQuery.refetch()} />;
  }

  const headerCopy = isStoreManager
    ? {
        title: "Team",
        sub: "Invite store associates and field auditors to your stores. Tenant admins manage roles and password resets.",
      }
    : {
        title: "Team",
        sub: "Members of your tenant. Admins can change roles or trigger a password reset email.",
      };

  // Self row never gets the role-change select or the password-reset action.
  // A sole tenant_admin demoting themselves would lock the tenant out, and
  // self-reset is just /forgot-password with extra clicks.
  const selfUserId = user?.id ?? "";

  // Members table columns vary by viewer role — store_manager has no
  // role-change select and no reset-password action.
  const columns = [
    {
      key: "email",
      header: "Email",
      cell: (r: TenantUserInfo) => (
        <div className="flex flex-col">
          <span>{r.email}</span>
          {r.fullName ? <span className="text-xs text-muted-foreground">{r.fullName}</span> : null}
        </div>
      ),
    },
    {
      key: "role",
      header: "Role",
      cell: (r: TenantUserInfo) => {
        const isSelf = r.userId === selfUserId;
        return isAdmin && !isSelf ? (
          <Select
            value={r.role}
            onValueChange={(role) =>
              updateRole.mutate(
                { companyUserId: r.companyUserId, role: role as UserRole },
                {
                  onSuccess: () =>
                    toast.success(`Role updated to ${ROLE_LABELS[role as UserRole] ?? role}`),
                },
              )
            }
            disabled={updateRole.isPending}
          >
            <SelectTrigger className="w-44 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(ROLE_LABELS) as UserRole[]).map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span className="flex items-center gap-2">
            {ROLE_LABELS[r.role] ?? r.role}
            {isSelf ? (
              <span className="text-xs text-muted-foreground">(you)</span>
            ) : null}
          </span>
        );
      },
    },
    { key: "joined", header: "Joined", cell: (r: TenantUserInfo) => formatRelative(r.createdAt) },
    {
      key: "actions",
      header: "",
      cell: (r: TenantUserInfo) => {
        const isSelf = r.userId === selfUserId;
        if (!isAdmin || isSelf) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                adminReset.mutate(r.userId, {
                  onSuccess: (data) => {
                    toast.success(
                      "Reset email sent",
                      data.devLink ? `Dev link: ${data.devLink}` : undefined,
                    );
                  },
                });
              }}
              disabled={adminReset.isPending}
            >
              <KeyRound className="mr-2 h-3 w-3" />
              Send reset
            </Button>
            <ConfirmDialog
              destructive
              title={`Remove ${r.email}?`}
              description={
                `This archives ${r.email}'s tenant membership and every store binding ` +
                `they have here. They lose access immediately on their next request. The action ` +
                `can be reversed by re-inviting them.`
              }
              confirmLabel="Remove member"
              onConfirm={() => {
                void removeMember.mutateAsync(r.companyUserId);
              }}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={removeMember.isPending}
                  aria-label={`Remove ${r.email}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              }
            />
          </div>
        );
      },
      className: "w-56",
    },
  ];

  // Pending invitations table (visible for inviting roles).
  const invitationColumns = [
    {
      key: "email",
      header: "Email",
      cell: (r: InvitationSummary) => <span>{r.email}</span>,
    },
    {
      key: "role",
      header: "Role",
      cell: (r: InvitationSummary) => <span>{ROLE_LABELS[r.role] ?? r.role}</span>,
    },
    {
      key: "stores",
      header: "Stores",
      cell: (r: InvitationSummary) =>
        r.storeIds.length === 0 ? (
          <span className="text-xs text-muted-foreground">tenant-wide</span>
        ) : (
          <span className="text-xs">{r.storeIds.length} stores</span>
        ),
    },
    {
      key: "invitedBy",
      header: "Invited by",
      cell: (r: InvitationSummary) =>
        r.invitedByEmail ? <span className="text-xs">{r.invitedByEmail}</span> : <span>—</span>,
    },
    {
      key: "expires",
      header: "Expires",
      cell: (r: InvitationSummary) => formatRelative(r.expiresAt),
    },
    {
      key: "actions",
      header: "",
      cell: (r: InvitationSummary) => (
        <div className="flex items-center justify-end gap-2">
          {/* Reissue: mints a fresh token, resends the email, and copies the
              link to the clipboard — the raw token isn't stored, so this is the
              only way to recover a usable link after the invite was created. */}
          <Button
            variant="outline"
            size="sm"
            disabled={reissue.isPending}
            onClick={() => {
              void reissue.mutateAsync(r.id, {
                onSuccess: (data) => {
                  void navigator.clipboard?.writeText(data.inviteLink).catch(() => undefined);
                  toast.success(
                    "Invite link copied",
                    data.emailSent
                      ? `Fresh link copied and re-emailed to ${r.email}.`
                      : `Fresh link copied. Email couldn't be sent — share the link directly.`,
                  );
                },
                onError: (e) => toast.error(e.problem?.detail ?? "Failed to resend"),
              });
            }}
          >
            <Send className="mr-2 h-3 w-3" />
            Resend
          </Button>
          <ConfirmDialog
            destructive
            title={`Revoke invitation to ${r.email}?`}
            description={
              `${r.email} won't be able to use the invitation link anymore. They'll ` +
              `need a fresh invite from you (or another admin) to join. This action ` +
              `can't be undone.`
            }
            confirmLabel="Revoke invitation"
            onConfirm={() => {
              void revoke.mutateAsync(r.id, {
                onSuccess: () => toast.success("Invitation revoked"),
                onError: (e) => toast.error(e.problem?.detail ?? "Failed to revoke"),
              });
            }}
            trigger={
              <Button
                variant="outline"
                size="sm"
                disabled={revoke.isPending}
                className="text-destructive hover:text-destructive"
              >
                <X className="mr-2 h-3 w-3" />
                Revoke
              </Button>
            }
          />
        </div>
      ),
      className: "w-56",
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <PageHeader
        title={headerCopy.title}
        description={headerCopy.sub}
        actions={
          policy.canInvite ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite member
            </Button>
          ) : undefined
        }
      />

      {/* Backfill banner (tenant_admin only) — surfaces users with a
          store-scoped role and zero bindings. The banner links to the new
          per-user assignment endpoint via a future detail UI; for now the
          tenant_admin uses this list to identify who needs attention. */}
      {isAdmin && (unboundQuery.data?.items?.length ?? 0) > 0 ? (
        <Alert variant="default">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {unboundQuery.data?.items.length} user
            {unboundQuery.data && unboundQuery.data.items.length === 1 ? "" : "s"} need store
            assignment
          </AlertTitle>
          <AlertDescription>
            These users have a store-scoped role but no stores assigned and can&apos;t see anything:{" "}
            {unboundQuery.data?.items.slice(0, 5).map((u, i) => (
              <span key={u.userId}>
                {i > 0 ? ", " : ""}
                <span className="font-medium">{u.email}</span>
                <span className="text-muted-foreground"> ({ROLE_LABELS[u.role]})</span>
              </span>
            ))}
            {(unboundQuery.data?.items.length ?? 0) > 5 ? "…" : ""}
          </AlertDescription>
        </Alert>
      ) : null}

      <section className="space-y-2">
        <h2 className="text-sm font-medium text-muted-foreground">Members</h2>
        <DataTable
          rowKey={(r) => r.companyUserId}
          rows={tenantUsersQuery.data?.items}
          loading={tenantUsersQuery.isLoading}
          columns={columns}
          emptyTitle="No team members yet"
        />
      </section>

      {(isAdmin || isStoreManager) ? (
        <section className="space-y-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Pending invitations
          </h2>
          <DataTable
            rowKey={(r) => r.id}
            rows={invitationsQuery.data?.items}
            loading={invitationsQuery.isLoading}
            columns={invitationColumns}
            emptyTitle="No pending invitations"
          />
        </section>
      ) : null}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={inviteForm.handleSubmit((data) => {
              // Strip storeIds for tenant-wide roles so we never send a stale
              // selection from a prior store-scoped pick.
              const storeIds = isStoreScopedRole(data.role as UserRole)
                ? data.storeIds
                : [];
              if (isStoreScopedRole(data.role as UserRole) && storeIds.length === 0) {
                toast.error("Pick at least one store for this role");
                return;
              }
              const payload: CreateInvitationRequest = {
                email: data.email,
                role: data.role,
                storeIds,
              };
              invite.mutate(payload);
            })}
          >
            <FieldStack>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="teammate@company.com"
                {...inviteForm.register("email", { required: true })}
              />
            </FieldStack>
            <FieldStack>
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.watch("role")}
                onValueChange={(role) =>
                  inviteForm.setValue("role", role as InviteForm["role"], { shouldDirty: true })
                }
              >
                <SelectTrigger id="invite-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {policy.allowedRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {ROLE_LABELS[r]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldStack>
            {showStorePicker ? (
              <FieldStack>
                <Label htmlFor="invite-stores">Stores</Label>
                <MultiStorePicker
                  id="invite-stores"
                  value={selectedStoreIds}
                  onValueChange={(ids) =>
                    inviteForm.setValue("storeIds", ids, { shouldDirty: true })
                  }
                  allowedIds={policy.storeMode === "scoped" ? policy.allowedStoreIds : undefined}
                  placeholder={
                    policy.storeMode === "scoped"
                      ? "Select from your stores"
                      : "Select stores"
                  }
                />
                <p className="text-xs text-muted-foreground">
                  This person will see and act on these stores only.
                </p>
              </FieldStack>
            ) : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Sending…" : "Send invitation"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
